import React from 'react';
import PropTypes from 'prop-types';
import useAffordance from 'Hooks/useAffordance';
import fr from 'facet-rest';
import { useMeetingContext } from 'Context/MeetingContext';
import useResource from 'Hooks/useResource';

const TranscriptContext = React.createContext(undefined);

const TranscriptProvider = ({
  children,
}) => {
  const { meetingResource } = useMeetingContext();

  const {
    loading,
    loadingError,
    collection,
    refreshCollection,
    setCollection,
  } = useAffordance(meetingResource?.relationship(fr.relationships.transcript.name)?.link('related'));
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const nextTranscript = () => {
    const length = collection?.resources().list().length || 0;
    if (length === 0 || selectedIndex === length - 1) {
      setSelectedIndex(0);
      return;
    }

    setSelectedIndex(prev => prev + 1);
  };

  const prevTranscript = () => {
    const length = collection?.resources().list().length || 0;
    if (length === 0) {
      setSelectedIndex(0);
      return;
    } if (selectedIndex - 1 < 0) {
      setSelectedIndex(length - 1);
      return;
    }

    setSelectedIndex(prev => prev - 1);
  };

  let selectedResource = null;
  if (selectedIndex < collection?.resources().length()) {
    selectedResource = collection?.resources().index(selectedIndex);
  }
  const { resource, setAttribute } = useResource(selectedResource);

  const memoizedValue = React.useMemo(() => {
    return {
      loading,
      loadingError,
      selectedIndex,
      selectedResource: resource,
      setAttributeOnSelectedResource: setAttribute,
      collection,
      refreshCollection,
      setCollection,
      nextTranscript,
      prevTranscript,
    };
  }, [
    selectedIndex,
    resource,
    setAttribute,
    loading,
    loadingError,
    collection,
    refreshCollection,
    setCollection,
    nextTranscript,
    prevTranscript,
  ]);

  return (
    <TranscriptContext.Provider value={memoizedValue}>
      {children}
    </TranscriptContext.Provider>
  );
};

// Use ItemList State Context
const useTranscriptContext = () => {
  const context = React.useContext(TranscriptContext);
  if (context === undefined) {
    throw new Error('useTranscriptContext must be used within a TranscriptProvider');
  }
  return context;
};

TranscriptProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  TranscriptProvider,
  useTranscriptContext,
};
